import { useCallback, useEffect, useMemo } from 'react'
import invariant from 'tiny-invariant'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { useSearchParams } from 'react-router-dom'
import { match } from 'ts-pattern'
import { Currency, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useDefaultCurrency, useEnabledCurrencies } from 'SettingsV2/index'
import { toMoney } from '@sequencehq/utils'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

type InvoiceMonth = string
type InvoiceModel = Dashboardv99990101Api.Invoices.Invoice

export type TotalChangeDirection = 'INCREASE' | 'DECREASE' | undefined

export type UseInvoicesGridData = {
  enabledCurrencies: Currency[]
  currency: Currency | undefined
  updateCurrency: (newCurrency: string) => void
  months: { label: string; value: string }[]
  customers: {
    id: InvoiceModel['customerId']
    name: InvoiceModel['customerLegalCompanyName']
  }[]
  getTotal: (filters?: { customerId?: string; month?: InvoiceMonth }) => string
  getInvoices: (filters?: {
    customerId?: string
    month?: InvoiceMonth
  }) => InvoiceModel['id'][]
  getNavigateableInvoices: (
    invoiceId: InvoiceModel['id']
  ) => InvoiceModel['id'][]
  getPrevMonthTotalChangeByCustomer: (filters: {
    customerId: string
    month: InvoiceMonth
  }) => TotalChangeDirection
  getPrevMonthTotalChangeByMonth: (filters: {
    month: InvoiceMonth
  }) => TotalChangeDirection
}

type UseInvoicesGrid = () => {
  loading: boolean
  data?: UseInvoicesGridData
}

export const useInvoicesGrid: UseInvoicesGrid = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const currencyResponse = useEnabledCurrencies()
  const defaultCurrency = useDefaultCurrency()

  const { enabledCurrencies, currency } = match(currencyResponse)
    .with({ status: 'SUCCESS' }, matched => ({
      enabledCurrencies: matched.enabledCurrencies,
      currency: matched.enabledCurrencies.find(
        enabledCurrency => enabledCurrency === searchParams.get('currency')
      )
    }))
    .otherwise(() => ({
      enabledCurrencies: [],
      currency: undefined
    }))

  useEffect(() => {
    if (!currency && defaultCurrency) {
      setSearchParams(prev => {
        const params = prev
        params.append('currency', defaultCurrency)
        return params
      })
    }
  }, [currency, defaultCurrency, setSearchParams])

  const invoicesGridQuery = useQuery(dashboard20240730Client.getInvoicesGrid, {
    invoiceCurrency: currency,
    invoiceStatuses: ['IN_PROGRESS', 'DRAFT', 'FINAL', 'SENT']
  })
  const invoiceOverviewResponse = invoicesGridQuery.data

  const invoicesTotals = useMemo(() => {
    if (!invoiceOverviewResponse) {
      return null
    }
    return invoiceOverviewResponse
  }, [invoiceOverviewResponse])

  const sortedCustomers = useMemo(() => {
    if (!invoicesTotals) {
      return []
    }

    return invoicesTotals.customerDetails
      .map(({ customerId, legalName, ...rest }) => ({
        id: customerId,
        name: legalName,
        ...rest
      }))
      .sort((a, b) => {
        if (a.name > b.name) {
          return 1
        }
        if (a.name < b.name) {
          return -1
        }
        return 0
      })
  }, [invoicesTotals])

  const invoicesPerMonth: Record<string, InvoiceModel['id'][]> = useMemo(() => {
    /**
     * We use the sorted customers as the basis so that the order of the invoices
     * will match the customer order in the grid.
     */
    return (
      sortedCustomers
        .flatMap(customerDetail => customerDetail.monthDetails)
        .reduce(
          (
            acc: Record<string, InvoiceModel['id'][]>,
            monthDetail: {
              month: string
              total: string
              invoiceIds: string[]
            }
          ) => {
            if (!acc[monthDetail.month]) {
              return {
                ...acc,
                [monthDetail.month]: monthDetail.invoiceIds
              }
            }

            return {
              ...acc,
              [monthDetail.month]: [
                ...acc[monthDetail.month],
                ...monthDetail.invoiceIds
              ]
            }
          },
          {}
        ) ?? {}
    )
  }, [sortedCustomers])

  const getInvoices = useCallback(
    ({
      customerId,
      month
    }: {
      customerId?: string
      month?: InvoiceMonth
    } = {}) => {
      if (!invoicesTotals) {
        return []
      }

      if (!customerId && month) {
        return invoicesPerMonth[month]
      }

      if (!month && customerId) {
        return (
          invoicesTotals.customerDetails
            .find(customer => customer.customerId === customerId)
            ?.monthDetails.flatMap(monthOverview => monthOverview.invoiceIds) ??
          []
        )
      }

      invariant(customerId, 'Must specify customer Id')
      invariant(month, 'Must specify month')

      return (
        invoicesTotals.customerDetails
          .find(customer => customer.customerId === customerId)
          ?.monthDetails.find(monthOverview => monthOverview.month === month)
          ?.invoiceIds ?? []
      )
    },
    [invoicesTotals, invoicesPerMonth]
  )

  const getNavigateableInvoices = useCallback(
    (invoiceId: InvoiceModel['id']) => {
      const invoicesForMonth = Object.values(invoicesPerMonth).find(invoices =>
        invoices.includes(invoiceId)
      ) ?? [invoiceId]

      return invoicesForMonth
    },
    [invoicesPerMonth]
  )

  const getTotal = useCallback(
    ({
      customerId,
      month
    }: {
      customerId?: string
      month?: InvoiceMonth
    } = {}): string | undefined => {
      if (!invoicesTotals) {
        return '0.00'
      }

      if (!customerId && !month) {
        return invoicesTotals.accountTotal
      }

      if (!customerId && month) {
        return invoicesTotals.monthDetails.find(
          monthOverview => monthOverview.month === month
        )?.total
      }

      if (!month && customerId) {
        return invoicesTotals.customerDetails.find(
          customer => customer.customerId === customerId
        )?.total
      }

      invariant(customerId, 'Must specify customer Id')
      invariant(month, 'Must specify month')

      return invoicesTotals.customerDetails
        .find(customer => customer.customerId === customerId)
        ?.monthDetails.find(monthOverview => monthOverview.month === month)
        ?.total
    },
    [invoicesTotals]
  )

  const getPrevMonthChangeDirection = useCallback(
    ({
      allMonths,
      currentMonth
    }: {
      allMonths: Array<{ month: InvoiceMonth; total: string }>
      currentMonth: InvoiceMonth
    }) => {
      const sortedMonthDetails = allMonths.sort((a, b) => {
        if (a.month > b.month) return 1
        if (a.month < b.month) return -1
        return 0
      })

      const thisMonthIndex = sortedMonthDetails.findIndex(
        monthDetail => monthDetail.month === currentMonth
      )

      if (thisMonthIndex === -1 || thisMonthIndex === 0) return undefined

      const thisMonth = sortedMonthDetails.at(thisMonthIndex)
      const lastMonth = sortedMonthDetails.at(thisMonthIndex - 1)

      if (!thisMonth || !lastMonth) return undefined

      const thisMonthTotal = Number(thisMonth.total)
      const lastMonthTotal = Number(lastMonth.total)

      if (thisMonthTotal > lastMonthTotal) return 'INCREASE'
      if (thisMonthTotal < lastMonthTotal) return 'DECREASE'

      return undefined
    },
    []
  )

  const getPrevMonthTotalChangeByMonth = useCallback(
    ({ month }: { month: InvoiceMonth }) => {
      if (!invoicesTotals) return undefined

      return getPrevMonthChangeDirection({
        allMonths: invoicesTotals.monthDetails,
        currentMonth: month
      })
    },
    [invoicesTotals, getPrevMonthChangeDirection]
  )

  const getPrevMonthTotalChangeByCustomer = useCallback(
    ({ customerId, month }: { customerId: string; month: InvoiceMonth }) => {
      const customerDetails = invoicesTotals?.customerDetails.find(
        customer => customer.customerId === customerId
      )

      if (!customerDetails) return undefined

      return getPrevMonthChangeDirection({
        allMonths: customerDetails.monthDetails,
        currentMonth: month
      })
    },
    [invoicesTotals, getPrevMonthChangeDirection]
  )

  const getFormattedTotal = useCallback(
    ({
      customerId,
      month
    }: {
      customerId?: string
      month?: InvoiceMonth
    } = {}): string => {
      if (!currency) {
        return ''
      }
      const total = getTotal({
        customerId,
        month
      })
      const invoices =
        month || customerId
          ? getInvoices({
              customerId,
              month
            })
          : []

      if (!total && !invoices?.length) {
        return ''
      }

      return toMoney({ value: `${total ?? 0.0}`, currency })
    },
    [getTotal, currency, getInvoices]
  )

  const months = useMemo(() => {
    if (!invoicesTotals) {
      return []
    }
    return invoicesTotals.monthDetails
      .map(monthOverview => ({
        label: dateTimeWithFormat(monthOverview.month, 'MMM yy'),
        value: monthOverview.month
      }))
      .sort((a, b) => {
        if (a.value > b.value) {
          return 1
        }
        if (a.value < b.value) {
          return -1
        }
        return 0
      })
  }, [invoicesTotals])

  const updateCurrency = (newCurrency: string) => {
    setSearchParams(values => ({
      ...Object.fromEntries(values),
      currency: newCurrency
    }))
  }

  return {
    loading: invoicesGridQuery.isPending,
    data: {
      enabledCurrencies,
      currency,
      updateCurrency,
      getInvoices,
      getTotal: getFormattedTotal,
      getNavigateableInvoices,
      months,
      customers: sortedCustomers,
      getPrevMonthTotalChangeByCustomer,
      getPrevMonthTotalChangeByMonth
    }
  }
}
